import React, { useEffect, useState } from 'react';
import Modal from '../../modals/Modal';
import Spinner from '../../Spinner';
import ViewPlanModal from './ViewPlanModal';
import { fetchConferenceSubscriptionTeams } from '../../../services/admin/subscriptionTrackerApi';
import {
  ConferenceSubscriptionTeamItem,
  ConferenceSubscriptionTeamsResponse,
} from '../../../types/admin/subscriptionTracker';
import { getSportLabel } from '../../../utils/admin/formatSubscriptionDate';

interface ConferenceTeamsModalProps {
  isOpen: boolean;
  sportCode: string;
  conferenceName: string;
  conferenceShortName?: string;
  onClose: () => void;
}

function NotDidBadges({ team }: { team: ConferenceSubscriptionTeamItem }) {
  const badges: React.ReactNode[] = [];

  if (team.team_id == null) {
    badges.push(
      <span key="unmatched" className="px-1.5 py-0.5 text-[10px] rounded bg-gray-200 text-gray-700">
        Unmatched
      </span>
    );
  }
  if (team.ever_invoiced) {
    badges.push(
      <span key="invoiced" className="px-1.5 py-0.5 text-[10px] rounded bg-blue-100 text-blue-800">
        Ever invoiced
      </span>
    );
  } else {
    badges.push(
      <span key="never" className="px-1.5 py-0.5 text-[10px] rounded bg-gray-100 text-gray-600">
        Never invoiced
      </span>
    );
  }
  if (team.is_paid_subscribed) {
    badges.push(
      <span key="paid" className="px-1.5 py-0.5 text-[10px] rounded bg-green-100 text-green-800">
        Paid subscribed
      </span>
    );
  } else if (team.ever_invoiced) {
    badges.push(
      <span key="not-paid" className="px-1.5 py-0.5 text-[10px] rounded bg-amber-100 text-amber-800">
        Not paid subscribed
      </span>
    );
  }

  return <span className="inline-flex flex-wrap gap-1">{badges}</span>;
}

function TeamRow({
  team,
  variant,
  onViewPlan,
}: {
  team: ConferenceSubscriptionTeamItem;
  variant: 'did' | 'not-did';
  onViewPlan: (team: ConferenceSubscriptionTeamItem) => void;
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 hover:bg-gray-50">
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-900">{team.team_name}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
          {team.team_code != null && <span>Code {team.team_code}</span>}
          {team.team_id != null && <span>ID {team.team_id}</span>}
          {variant === 'not-did' && <NotDidBadges team={team} />}
        </div>
      </div>
      {team.team_id != null && (
        <button
          type="button"
          onClick={() => onViewPlan(team)}
          className="shrink-0 px-2.5 py-1 text-xs text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50"
        >
          View Plan
        </button>
      )}
    </li>
  );
}

function TeamSection({
  title,
  count,
  teams,
  variant,
  emptyLabel,
  onViewPlan,
}: {
  title: string;
  count: number;
  teams: ConferenceSubscriptionTeamItem[];
  variant: 'did' | 'not-did';
  emptyLabel: string;
  onViewPlan: (team: ConferenceSubscriptionTeamItem) => void;
}) {
  return (
    <section className="border border-gray-200 rounded-lg overflow-hidden">
      <header
        className={`px-3 py-2 text-sm font-semibold ${
          variant === 'did' ? 'bg-green-50 text-green-900' : 'bg-slate-50 text-slate-800'
        }`}
      >
        {title}
        <span className="ml-2 font-normal text-xs opacity-80">({count})</span>
      </header>
      {teams.length === 0 ? (
        <p className="px-3 py-6 text-sm text-center text-gray-500">{emptyLabel}</p>
      ) : (
        <ul className="divide-y divide-gray-100 bg-white max-h-72 overflow-y-auto">
          {teams.map((team, index) => (
            <TeamRow
              key={`${team.team_id ?? 'x'}-${team.team_name}-${index}`}
              team={team}
              variant={variant}
              onViewPlan={onViewPlan}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

const ConferenceTeamsModal: React.FC<ConferenceTeamsModalProps> = ({
  isOpen,
  sportCode,
  conferenceName,
  conferenceShortName = '',
  onClose,
}) => {
  const [data, setData] = useState<ConferenceSubscriptionTeamsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planTeam, setPlanTeam] = useState<{ teamId: number; teamName: string } | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setData(null);
      setError(null);
      setPlanTeam(null);
      return;
    }

    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError(null);

      const result = await fetchConferenceSubscriptionTeams(
        {
          sport_code: sportCode,
          conference_name: conferenceName,
        },
        controller.signal
      );

      if (result.aborted || controller.signal.aborted) return;

      if (result.data) {
        setData(result.data);
      } else {
        setData(null);
        setError(result.error ?? 'Failed to load conference teams');
      }

      setLoading(false);
    };

    load();

    return () => {
      controller.abort();
    };
  }, [isOpen, sportCode, conferenceName]);

  const titleShort = data?.conference_short_name || conferenceShortName;
  const titleFull = data?.conference_name || conferenceName;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        closeOnBackdrop={!planTeam}
        closeOnEsc={!planTeam}
        panelClassName="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col relative mx-4"
        backdropClassName="fixed inset-0 flex items-center justify-center bg-black/50 z-50 px-4"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mr-8">{titleFull}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {getSportLabel(sportCode)} ({sportCode})
            {titleShort && titleShort !== titleFull ? ` · ${titleShort}` : ''}
            {data
              ? ` · ${data.teams_did_count} subscribed / ${data.teams_total} total`
              : ''}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading && (
            <div className="flex justify-center items-center h-40">
              <Spinner size="md" />
              <span className="ml-2 text-gray-600">Loading teams…</span>
            </div>
          )}

          {error && !loading && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>
          )}

          {data && !loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TeamSection
                title="Subscribed"
                count={data.teams_did_count}
                teams={data.teams_did}
                variant="did"
                emptyLabel="No subscribed teams for this sport."
                onViewPlan={(team) => {
                  if (team.team_id != null) {
                    setPlanTeam({ teamId: team.team_id, teamName: team.team_name });
                  }
                }}
              />
              <TeamSection
                title="Not subscribed"
                count={data.teams_not_did_count}
                teams={data.teams_not_did}
                variant="not-did"
                emptyLabel="Every roster team is subscribed."
                onViewPlan={(team) => {
                  if (team.team_id != null) {
                    setPlanTeam({ teamId: team.team_id, teamName: team.team_name });
                  }
                }}
              />
            </div>
          )}
        </div>
      </Modal>

      <ViewPlanModal
        isOpen={planTeam != null}
        teamId={planTeam?.teamId ?? null}
        teamName={planTeam?.teamName ?? ''}
        onClose={() => setPlanTeam(null)}
      />
    </>
  );
};

export default ConferenceTeamsModal;
